import React from 'react';
import { Container, Card, Alert, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getProductById, addToCart } from '../../store';
import AddedToCartModal from '../Cart/AddedToCartModal';
import ItemQuantity from '../Cart/ItemQuantity';
import ProductReviews from './ProductReviews';
import ProductCarousel from './ProductCarousel';
import StarRating from './StarRating';
import { MAX_QTY_PER_ITEM, PRODUCT_GALLERY_IMAGES } from '../../constants';
import { parseProductCopy } from '../../utils/productCopy';

class SingleProduct extends React.Component {
  constructor() {
    super();
    this.state = {
      show: false,
      alert: false,
      addError: false,
      adding: false,
      remainingQuantityAllowed: MAX_QTY_PER_ITEM,
      quantity: null,
      averageRating: 0,
      reviewCount: 0,
    };
    this.handleClose = this.handleClose.bind(this);
    this.showAlert = this.showAlert.bind(this);
    this.hideAlert = this.hideAlert.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.handleReviewSummaryChange = this.handleReviewSummaryChange.bind(this);
  }
  componentDidMount() {
    this.props.getProduct(this.props.productId);
  }
  async addToCart(evt, quantity) {
    evt.preventDefault();
    if (this.state.adding) return;

    const qty = parseInt(quantity, 10);
    if (!qty || qty < 1) return;

    const product = this.props.product;
    const cartItems = this.props.cartItems || [];
    let verifiedQty = true;

    for (let i = 0; i < cartItems.length; i++) {
      let cartItem = cartItems[i];
      if (cartItem.name === product.name) {
        if (
          parseInt(cartItem.quantity, 10) + qty >
          MAX_QTY_PER_ITEM
        ) {
          const remainingQuantityAllowed =
            MAX_QTY_PER_ITEM - parseInt(cartItem.quantity, 10);
          this.showAlert(remainingQuantityAllowed);
          verifiedQty = false;
        }
        break;
      }
    }

    if (!verifiedQty) return;

    this.setState({ adding: true, addError: false });
    try {
      await this.props.addToCart({ product, quantity: qty });
      this.hideAlert();
      this.handleShow(qty);
    } catch (err) {
      console.error(err);
      this.setState({ addError: true });
    } finally {
      this.setState({ adding: false });
    }
  }
  showAlert(remainingQuantityAllowed) {
    this.setState({ remainingQuantityAllowed });
    this.setState({ alert: true });
  }
  hideAlert() {
    this.setState({ alert: false });
  }
  handleClose() {
    this.setState({ show: false });
  }
  handleShow(quantity) {
    this.setState({ show: true, quantity });
  }
  handleReviewSummaryChange({ averageRating, reviewCount }) {
    this.setState({ averageRating, reviewCount });
  }
  render() {
    const product = this.props.product;
    const show = this.state.show;
    const handleClose = this.handleClose;
    const { oneLiners, productDetails } = parseProductCopy(product.description);

    return (
      <Container className="Center-column product-page">
        <div className="pg-break"> </div>
        <div className="product-page-layout">
          <Card border="light" className="product-page-image marg">
            <ProductCarousel images={PRODUCT_GALLERY_IMAGES} interval={4000} />
          </Card>
          <Card border="light" className="product-page-info marg">
            <Card.Body>
              <Card.Title className="product-page-name">{product.name}</Card.Title>
              <Card.Text className="product-page-price">${product.price}</Card.Text>
              {this.state.reviewCount > 0 && (
                <div className="product-page-rating">
                  <StarRating value={this.state.averageRating} size="sm" />
                  <span>
                    {this.state.averageRating.toFixed(1)} ({this.state.reviewCount})
                  </span>
                </div>
              )}
              <div className="product-oneliners">
                {oneLiners.map((section, i) => (
                  <p key={i} className="product-oneliner">
                    {section.header ? `${section.header} ` : ''}
                    {section.body}
                  </p>
                ))}
              </div>
              {productDetails.length > 0 && (
                <a href="#product-details" className="product-details-link">
                  Product Details
                </a>
              )}
              <ItemQuantity
                inventory={product.inventory}
                quantity={1}
                addToCart={this.addToCart}
                adding={this.state.adding}
                type="add"
              />
              <div className="pg-break"> </div>
              {this.state.addError && (
                <Alert variant="danger">
                  Could not add this item to your bag. Please try again.
                </Alert>
              )}
              {this.state.alert && this.state.remainingQuantityAllowed >= 1 && (
                <Alert variant="danger">
                  {`That quantity is currently unavailable. Please choose ${this.state.remainingQuantityAllowed} or less.`}
                </Alert>
              )}
              {this.state.alert &&
                this.state.remainingQuantityAllowed === 0 && (
                  <Alert variant="danger">
                    {`You've already added the maximum available quantity to your bag.`}
                  </Alert>
                )}
            </Card.Body>
          </Card>
        </div>
        {productDetails.length > 0 && (
          <section id="product-details" className="product-details-block marg">
            <h2 className="product-details-title">Product Details</h2>
            <div className="product-details">
              {productDetails.map((section, i) => (
                <div key={i} className="product-details-section">
                  {section.header && (
                    <p className="product-details-header">{section.header}</p>
                  )}
                  {section.body && (
                    <p className="product-details-body">{section.body}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        {product.id && (
          <ProductReviews
            productId={product.id}
            onSummaryChange={this.handleReviewSummaryChange}
          />
        )}
        <Modal
          backdrop={false}
          show={show}
          onHide={handleClose}
          centered="true"
          dialogClassName="added-to-bag-dialog"
        >
          <AddedToCartModal
            product={product}
            handleClose={handleClose}
            quantity={this.state.quantity}
          />
        </Modal>
      </Container>
    );
  }
}

/**
 * CONTAINER
 */
const mapState = (state, ownProps) => {
  return {
    product: state.products,
    productId: ownProps.match.params.productId,
    cartItems: state.cartItems,
  };
};

const mapDispatch = (dispatch) => {
  return {
    getProduct: (productId) => dispatch(getProductById(productId)),
    addToCart: (product) => dispatch(addToCart(product)),
  };
};

export default connect(mapState, mapDispatch)(SingleProduct);

/**
 * PROP TYPES
 */
SingleProduct.propTypes = {
  product: PropTypes.object,
};
